import { Pagination } from '@mui/material';
import { Tracking } from '@vromo/analytics/tracking';
import { useStore } from '@vromo/mobx-react/mobx-react';
import Button from '@vromo/ui/components/Button';
import Container from '@vromo/ui/components/Container';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStore } from '../../stores/RootStore';
import { Filters } from '../utils/reportTypes';
import AuditHistoryModal from './components/AuditHistoryModal';
import ChatHistoryModal from './components/ChatHistoryModal';
import JobHistoryTable from './components/JobHistoryTable';
import PhotoHistoryModal from './components/PhotoHistoryModal';
import { JobHistoryModal, JobHistoryResponse } from './helpers/JobHistoryHelper';
import useFetchJobHistory from './helpers/useFetchJobHistory';
import styles from './jobHistory.style';

interface Props {
    createJobCsv: () => void;
    filters: Filters;
}

const JobHistory = observer<Props>(({ filters }) => {
    const { t } = useTranslation();
    const { authStore } = useStore<RootStore>('rootStore');
    const { id: groupId } = authStore.role.group || {};

    const [selectedJob, setSelectedJob] = useState<JobHistoryResponse | undefined>(undefined);
    const [activeModal, setActiveModal] = useState<JobHistoryModal>(null);
    const closeModal = useCallback(() => setActiveModal(null), []);

    const {
        applyFiltersForJobHistory,
        downloadCSV,
        isLoading,
        isLoadingCSV,
        jobReports,
        pageNumber,
        totalPages
    } = useFetchJobHistory({ filters, groupId });

    const onApplyFilters = async (newPageNumber?: number) =>
        await applyFiltersForJobHistory(newPageNumber);

    const onChangePageNumber = (_: React.ChangeEvent<unknown>, p: number) => onApplyFilters(p - 1);

    useEffect(() => Tracking.action('Job History Report tab opened.'), []);

    useEffect(() => {
        onApplyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const modalProps = { selectedJob, onClose: closeModal };

    return (
        <Container className={styles.container} title={t('jobHistory.title')}>
            <div className={styles.header}>
                <Button disabled={isLoadingCSV} loading={isLoadingCSV} onClick={downloadCSV}>
                    {t('jobHistory.exportCSV')}
                    <FontAwesome className={styles.exportIcon} icon="download" />
                </Button>
            </div>
            <div className={styles.gridHolder}>
                {isLoading && (
                    <div className={styles.loading}>
                        {t('jobHistory.loading')}
                        <LoadingIndicator />
                    </div>
                )}
                {!isLoading && jobReports.length < 1 && (
                    <div className={styles.loading}>{t('jobHistory.noResults')}</div>
                )}
                {!isLoading && jobReports.length > 0 && (
                    <>
                        <JobHistoryTable
                            {...{
                                jobReports,
                                setSelectedJob,
                                setActiveModal
                            }}
                        />
                        {totalPages > 1 && (
                            <Pagination
                                className={styles.pagination}
                                variant="outlined"
                                shape="rounded"
                                count={totalPages}
                                page={pageNumber + 1}
                                onChange={onChangePageNumber}
                            />
                        )}
                    </>
                )}
            </div>
            <PhotoHistoryModal isOpen={activeModal === 'PHOTO'} {...modalProps} />
            <ChatHistoryModal isOpen={activeModal === 'CHAT'} {...modalProps} />
            <AuditHistoryModal isOpen={activeModal === 'AUDIT'} {...modalProps} />
        </Container>
    );
});

export default JobHistory;
